import { useEffect, useState } from 'react';
import { InfoDialogUI } from '../../components/InfoDialog/InfoDialogUI';
import { CheckCircleIcon } from '../../components/icons/CheckCircleIcon';
import { useUserContext } from '../../contexts/UserContext';
import { getLocalStorage, setLocalStorage } from '../../helpers/jsHelper';
import { FREE_TRIAL_POPUP_SHOWN_KEY } from '../../constants/projectConstants';
import styles from './FreeTrialCongratsPopup.module.scss';

// Shown once per org after a free trial is activated. Key is preserved across
// logout (see UserContext logout()) so it doesn't reappear on relogin.
export default function FreeTrialCongratsPopup() {
  const { currentUser, freeTrialInfo, updateUserData } = useUserContext();
  const [show, setShow] = useState(false);
  const orgId = currentUser?.organization;

  useEffect(() => {
    if (!orgId || !freeTrialInfo?.isFreeTrialStarted || freeTrialInfo?.isFreeTrialExpired) return;
    // wait for the day count to load so we never flash the generic "Your free trial
    // is now active" text before swapping to the "Your N-day free trial..." version
    if (!freeTrialInfo?.freeTrialDays) return;

    const shownForOrgIds = getLocalStorage(FREE_TRIAL_POPUP_SHOWN_KEY, true) || [];
    if (shownForOrgIds.includes(orgId)) return;

    setShow(true);
    setLocalStorage(FREE_TRIAL_POPUP_SHOWN_KEY, [...shownForOrgIds, orgId]);
    // trial start date/days may not be populated yet right after org creation -
    // refresh currentUser now so the countdown banner is ready by the time this closes
    updateUserData();
  }, [orgId, freeTrialInfo?.isFreeTrialStarted, freeTrialInfo?.freeTrialDays]);

  if (!show) return null;

  const days = freeTrialInfo?.freeTrialDays;

  const handleClose = () => {
    setShow(false);
    // safety net in case the fetch above raced with the backend's trial activation
    updateUserData();
  };

  return (
    <InfoDialogUI
      show={show}
      hideTitle
      size="md"
      onClose={handleClose}
      buttonText="Start exploring"
      onButtonClick={handleClose}>
      <div className={styles.congratsPopup}>
        <div className={styles.iconCircle}>
          <CheckCircleIcon />
        </div>
        <h3>Congratulations!</h3>
        <p>
          {days ? `Your ${days}-day free trial` : 'Your free trial'} is now active. Start
          creating, explore the platform, and experience everything Imagine.io has to offer.
        </p>
      </div>
    </InfoDialogUI>
  );
}
